import { useEffect, useRef, memo } from 'react';
import DOMPurify from 'dompurify';

// MathJax global config — loaded once
if (!window.MathJax) {
  window.MathJax = {
    tex: {
      inlineMath: [['$', '$']],
      displayMath: [['$$', '$$']],
      packages: { '[+]': ['mhchem'] },
    },
    loader: { load: ['[tex]/mhchem'] },
    startup: { typeset: false },
    options: {
      enableMenu: false,
    },
  };

  const script = document.createElement('script');
  script.src = 'https://cdn.jsdelivr.net/npm/mathjax@3/es5/tex-chtml.js';
  script.async = true;
  document.head.appendChild(script);
}

const PURIFY_CONFIG = {
  ADD_TAGS: ['mjx-container', 'mjx-math', 'mjx-mrow', 'mjx-mi', 'mjx-mn', 'mjx-mo', 'mjx-msup', 'mjx-msub', 'mjx-mfrac', 'mjx-msqrt', 'mjx-mroot', 'mjx-mtable', 'mjx-mtr', 'mjx-mtd', 'mjx-mtext', 'mjx-mover', 'mjx-munder', 'mjx-munderover', 'mjx-mspace', 'mjx-mpadded', 'mjx-mstyle', 'mjx-assistive-mml', 'math', 'semantics', 'mrow', 'mi', 'mn', 'mo', 'msup', 'msub', 'mfrac', 'msqrt', 'mroot', 'mtable', 'mtr', 'mtd', 'mtext', 'annotation', 'mover', 'munder', 'munderover', 'mspace', 'mpadded', 'mstyle'],
  ADD_ATTR: ['xmlns', 'mathvariant', 'encoding', 'stretchy', 'fence', 'separator', 'lspace', 'rspace', 'accent', 'accentunder', 'aria-hidden', 'style', 'data-mjx-texclass', 'jax', 'display', 'width', 'height'],
  FORBID_TAGS: ['script', 'style', 'iframe', 'object', 'embed', 'form'],
  FORBID_ATTR: ['onerror', 'onclick', 'onload', 'onmouseover'],
};

function escapeHtml(s) {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

/**
 * FormulaRenderer — MathJax 4 + mhchem
 * Renders text containing $LaTeX$ or $\ce{mhchem}$ formulas.
 * Plain text without $ delimiters passes through unchanged.
 */
export const FormulaRenderer = memo(function FormulaRenderer({ text, className = '' }) {
  const containerRef = useRef(null);
  const rendered = useRef('');

  useEffect(() => {
    if (!text || !text.includes('$') || !containerRef.current) return;
    if (text === rendered.current) return;
    rendered.current = text;

    // Build safe HTML: escape non-formula parts, keep $...$ intact for MathJax
    const parts = [];
    let lastIdx = 0;
    const regex = /\$\$([^$]+?)\$\$|\$([^$]+?)\$/g;
    let match;
    while ((match = regex.exec(text)) !== null) {
      if (match.index > lastIdx) {
        parts.push(escapeHtml(text.slice(lastIdx, match.index)));
      }
      // Keep the $...$ or $$...$$ intact for MathJax to process
      parts.push(match[0]);
      lastIdx = match.index + match[0].length;
    }
    if (lastIdx < text.length) {
      parts.push(escapeHtml(text.slice(lastIdx)));
    }

    const rawHtml = parts.join('');
    containerRef.current.innerHTML = DOMPurify.sanitize(rawHtml, PURIFY_CONFIG);

    // Typeset with MathJax
    if (window.MathJax?.typesetPromise) {
      window.MathJax.typesetPromise([containerRef.current]).catch(() => {});
    } else {
      // MathJax not loaded yet, retry after a short delay
      const retry = setInterval(() => {
        if (window.MathJax?.typesetPromise) {
          clearInterval(retry);
          window.MathJax.typesetPromise([containerRef.current]).catch(() => {});
        }
      }, 200);
      setTimeout(() => clearInterval(retry), 5000);
    }
  }, [text]);

  // No formulas — plain text
  if (!text || !text.includes('$')) {
    return <span className={className}>{text || ''}</span>;
  }

  return <span ref={containerRef} className={className} />;
});
